import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, LayoutDashboard, ShieldAlert, ClipboardCheck, FileText, Network, CalendarClock,
  FileSearch, Users, AlertTriangle, LifeBuoy, CalendarDays, ListChecks, FileDown, Loader2, Briefcase,
  CircleDot, Sparkles,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { FadeIn } from "@/components/Reveal";

const FEATURES = [
  { icon: LayoutDashboard, title: "Business Compliance Dashboard", desc: "A central dashboard summarizing risk levels, audit activity, overdue corrective actions, and upcoming compliance deadlines." },
  { icon: ShieldAlert, title: "Risk Register & Assessment Tool", desc: "Identify business risks, rate their likelihood and potential impact, calculate risk scores, assign responsible individuals, and document mitigation strategies — with automatic formulas and a color-coded 5×5 risk assessment matrix." },
  { icon: ClipboardCheck, title: "Audit Register", desc: "Maintain records of internal reviews, external audits, findings, corrective actions, assigned owners, and review deadlines." },
  { icon: FileText, title: "SOP Templates & Inventory", desc: "Editable Word templates to document operating procedures and establish responsibilities, plus an inventory tracking ownership, approval status, revision history, and review dates." },
  { icon: Network, title: "Organizational Chart & RACI Matrix", desc: "Organize reporting relationships and clearly establish responsibility for important business processes." },
  { icon: CalendarClock, title: "License, Permit & Insurance Tracker", desc: "Track licenses, insurance policies, certifications, expiration dates, and responsible parties — with automatic renewal alerts." },
  { icon: FileSearch, title: "Contract Inventory", desc: "Record and organize information about existing business contracts, including obligations, owners, expiration dates, and document locations. MITIVISE never generates, drafts, or modifies contracts." },
  { icon: Users, title: "Vendor & Contractor Register", desc: "Monitor vendor information, required documentation, certificates, and review dates." },
  { icon: AlertTriangle, title: "Incident & Corrective Action Tracker", desc: "Document incidents, operational issues, corrective actions, and resolution status." },
  { icon: LifeBuoy, title: "Business Continuity Planning", desc: "Use editable templates to establish operational response procedures for unexpected disruptions." },
  { icon: CalendarDays, title: "Compliance Calendar", desc: "Monitor key renewal, review, inspection, and audit dates through an interconnected worksheet." },
  { icon: ListChecks, title: "30/60/90 Day Action Plan", desc: "Prioritize operational improvements and document progress toward completing important business infrastructure tasks." },
];

const CAPABILITIES_AVAILABLE = [
  "Risk registers and risk management planning",
  "Audit tracking and corrective action logs",
  "SOP creation and organization",
  "Compliance calendars and deadline tracking",
  "Document management",
  "Business readiness assessments",
  "Contract tracking and secure document storage",
  "Operational dashboards",
  "Risk and compliance reporting",
];

const CAPABILITIES_PLANNED = [
  "AI-assisted SOP creation",
  "AI risk identification suggestions",
  "AI-assisted analysis of uploaded contracts",
  "Automated reminders (email + in-app)",
  "Vendor compliance tracking in the platform",
  "Organizational chart and RACI in the platform",
];

const FUNCTIONALITY = [
  "Automated risk scoring", "Dropdown selections", "Conditional formatting", "Linked workbook worksheets",
  "Deadline tracking", "Editable SOP documents", "Color-coded risk indicators", "Organized business records",
  "Printable and exportable reports",
];

const AUDIENCES = [
  "Entrepreneurs and new business owners", "Construction and skilled trade contractors",
  "Home healthcare and nonmedical service providers", "Food service businesses",
  "Courier and transportation companies", "Professional service providers",
  "Growing small businesses without a dedicated compliance department",
];

const INCLUDED = [
  "One integrated, functional Microsoft Excel-compatible workbook with interconnected tracking worksheets.",
  "Editable Word-compatible SOP templates.",
  "Organizational chart template.",
  "Business continuity planning template.",
  "Quick-start instruction guide in PDF format.",
  "Completed sample entries demonstrating how to use the system.",
  "A license for use within one purchasing business.",
];

const FAQS = [
  { q: "Is MITIVISE a monthly subscription?", a: "The MITIVISE Toolkit is a one-time purchase of $79 — no subscription required. A MITIVISE Workspace with expanded digital functionality is planned for the future; pricing will be announced when it launches." },
  { q: "Do I need special software?", a: "The toolkit is designed for compatible versions of Microsoft Excel and Word. Basic spreadsheet functionality may work in other applications, but compatibility should be tested before purchasing." },
  { q: "Can I customize the toolkit for my business?", a: "Yes. The spreadsheets and templates are editable so you can document your own business activities, risks, and operating procedures." },
  { q: "Does MITIVISE provide legal advice?", a: "No. MITIVISE is a business organization and risk management resource. It does not replace advice from qualified legal, insurance, accounting, or regulatory professionals." },
  { q: "Does MITIVISE create contracts?", a: "No. MITIVISE can track and store information about existing contracts, but it never generates, drafts, modifies, or interprets contracts, and it does not replace licensed attorneys." },
  { q: "Can I use MITIVISE without internet access?", a: "Yes. Once downloaded, the toolkit's Excel and Word files can be used offline." },
  { q: "Can I purchase additional services from TSM Group CLE?", a: "Yes. Customers can separately request eligible documentation, compliance consulting, notary, and workforce support services through TSM Group CLE." },
  { q: "How do I receive the toolkit after paying?", a: "You get an instant secure download link on the confirmation page and an order confirmation email with the same link. The link stays active for 30 days, and the files are yours to keep forever." },
];

export default function MitivisePage() {
  const [pricing, setPricing] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sample, setSample] = useState({ email: "", business_name: "", consent: false });
  const [sampleBusy, setSampleBusy] = useState(false);
  const [sampleDone, setSampleDone] = useState(false);
  const [sampleError, setSampleError] = useState("");

  useEffect(() => {
    document.title = "MITIVISE | Business Risk & Compliance Platform | TSM Group CLE";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "MITIVISE is a digital business risk management and compliance solution for small business owners: risk registers, audits, SOPs, compliance calendars, and document management. Download the toolkit for $79.");
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.setAttribute("data-testid", "cos-jsonld");
    ld.text = JSON.stringify({
      "@context": "https://schema.org", "@type": "Product",
      name: "MITIVISE Toolkit",
      brand: { "@type": "Brand", name: "TSM Group CLE" },
      description: "A downloadable business risk management and compliance toolkit for small businesses: risk register, audit register, SOP templates, license and insurance tracking, and more.",
      offers: { "@type": "Offer", price: "79.00", priceCurrency: "USD", availability: "https://schema.org/InStock" },
    });
    document.head.appendChild(ld);
    api.get("/api/compliance-os/pricing")
      .then((r) => setPricing(r.data))
      .catch(() => setError("Pricing is being updated — check back shortly."));
    return () => document.head.removeChild(ld);
  }, []);

  const price = pricing ? pricing.toolkit_price.toFixed(0) : "79";

  const buy = () => {
    setBusy(true);
    setError("");
    api
      .post("/api/payments/checkout", { lookup_key: "toolkit_onetime", origin_url: window.location.origin })
      .then((r) => (window.location.href = r.data.checkout_url))
      .catch((e) => {
        setBusy(false);
        if (e.response?.status === 401) {
          window.location.href = `/login?next=/mitivise`;
        } else {
          setError(formatApiErrorDetail(e.response?.data) || "Checkout could not start. Please try again.");
        }
      });
  };

  const getSample = async (e) => {
    e.preventDefault();
    setSampleBusy(true);
    setSampleError("");
    try {
      const res = await api.post("/api/sample-toolkit", sample, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "TSM_MITIVISE_Toolkit_SAMPLE.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setSampleDone(true);
    } catch (err) {
      let msg = "Download failed — please try again.";
      const d = err?.response?.data;
      if (typeof Blob !== "undefined" && d instanceof Blob) {
        try {
          const parsed = JSON.parse(await d.text());
          if (parsed?.detail) msg = formatApiErrorDetail(parsed.detail);
        } catch {
          /* keep default message */
        }
      } else if (d) {
        msg = formatApiErrorDetail(d);
      }
      setSampleError(msg);
    } finally {
      setSampleBusy(false);
    }
  };

  return (
    <div className="bg-paper" data-testid="compliance-os-product">
      <section className="noise-dark relative bg-navydeep pb-20 pt-14 text-white lg:pb-28 lg:pt-20">
        <div className="container-tsm relative grid max-w-[1200px] items-center gap-12 lg:grid-cols-2">
          <FadeIn>
            <p className="eyebrow">A TSM4 Solution by TSM Group CLE</p>
            <h1 className="mt-7 text-5xl font-extrabold leading-[1.05] tracking-tight sm:text-6xl" data-testid="cos-headline">
              MITIVISE
            </h1>
            <p className="mt-4 font-mono text-xs uppercase tracking-[0.3em] text-gold lg:text-sm" data-testid="cos-tagline">
              Know Your Risks. Build Your Systems. Protect Your Business.
            </p>
            <p className="mt-8 max-w-xl text-lg leading-relaxed text-white/70" data-testid="cos-intro">
              MITIVISE is a digital business risk management and compliance solution designed to help small business
              owners identify vulnerabilities, organize operational documentation, manage audits, and establish
              repeatable business processes.
            </p>
            <p className="mt-6 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-5 py-2 text-sm font-semibold text-gold" data-testid="cos-price-chip">
              ${price} — One-Time Download · No subscription required
            </p>
            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              <button type="button" onClick={buy} disabled={busy} data-testid="cos-buy-toolkit" className="btn-gold">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Get the MITIVISE Toolkit — ${price}
              </button>
              <a href="#included" data-testid="cos-see-included" className="btn-outline-light">See What's Included</a>
              <Link to="/login" data-testid="cos-signin" className="inline-flex items-center justify-center px-4 py-3.5 text-sm font-bold uppercase tracking-wide text-white/80 transition-colors hover:text-gold">
                Sign In
              </Link>
            </div>
            <p className="mt-6 max-w-xl text-xs leading-relaxed text-white/45">
              Secure checkout through Stripe. No account needed to purchase — your secure download link is emailed to
              you. MITIVISE organizes and tracks; it does not provide legal advice.
            </p>
          </FadeIn>
          <FadeIn delay={0.15} className="lg:justify-self-end">
            <figure className="rounded-lg border border-teal/30 bg-navy p-3 shadow-2xl">
              <img src="/previews/dashboard.png" alt="The MITIVISE Excel dashboard with live compliance metrics" className="w-full rounded-sm" />
              <figcaption className="px-2 py-3 text-center text-xs text-white/50">
                The actual MITIVISE Dashboard sheet (sample data shown).
              </figcaption>
            </figure>
          </FadeIn>
        </div>
      </section>

      <section id="capabilities" className="py-20 lg:py-28">
        <div className="container-tsm max-w-[1200px]">
          <FadeIn>
            <p className="eyebrow">Platform Capabilities</p>
            <h2 className="mt-6 max-w-3xl text-4xl font-extrabold text-navy lg:text-5xl">
              What MITIVISE Does
            </h2>
          </FadeIn>
          <div className="mt-10 grid gap-6 lg:grid-cols-2" data-testid="cos-capabilities">
            <FadeIn className="h-full">
              <div className="h-full rounded-xl border border-teal/25 bg-white p-8" data-testid="cos-capabilities-available">
                <p className="inline-flex items-center gap-2 rounded-full bg-teal/10 px-4 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-tealdeep">
                  <CircleDot className="h-3.5 w-3.5" /> Available Today
                </p>
                <ul className="mt-6 space-y-3.5">
                  {CAPABILITIES_AVAILABLE.map((c) => (
                    <li key={c} className="flex items-start gap-3 text-sm font-medium text-navy/85">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-teal" />
                      {c}
                    </li>
                  ))}
                </ul>
                <p className="mt-6 border-t border-navy/10 pt-4 text-xs leading-relaxed text-ink/55">
                  Available today through the MITIVISE Toolkit and the MITIVISE online workspace.
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={0.1} className="h-full">
              <div className="h-full rounded-xl border border-dashed border-navy/20 bg-paper p-8" data-testid="cos-capabilities-planned">
                <p className="inline-flex items-center gap-2 rounded-full border border-dashed border-amberdeep/50 bg-amber/10 px-4 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-amberdeep">
                  <Sparkles className="h-3.5 w-3.5" /> Planned / In Development
                </p>
                <ul className="mt-6 space-y-3.5">
                  {CAPABILITIES_PLANNED.map((c) => (
                    <li key={c} className="flex items-start gap-3 text-sm font-medium text-navy/60">
                      <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-amberdeep" />
                      {c}
                    </li>
                  ))}
                </ul>
                <p className="mt-6 border-t border-navy/10 pt-4 text-xs leading-relaxed text-ink/55">
                  Planned capabilities are under active development and are not yet part of the product. Availability
                  is not guaranteed and no launch date is promised.
                </p>
              </div>
            </FadeIn>
          </div>
          <FadeIn delay={0.15}>
            <p className="mt-8 rounded-lg border border-amber/50 bg-amber/10 px-6 py-4 text-sm leading-relaxed text-navy" data-testid="mitivise-contract-restriction">
              <strong>Important:</strong> MITIVISE never generates, drafts, or creates legal contracts. Its contract
              functionality is limited to analysis of existing uploaded contracts, administrative tracking, and secure
              storage. MITIVISE does not provide legal advice and does not replace licensed attorneys.
            </p>
          </FadeIn>
        </div>
      </section>

      <section id="included" className="border-y border-navy/5 bg-white py-20 lg:py-28">
        <div className="container-tsm max-w-[1200px]">
          <FadeIn>
            <p className="eyebrow">What's Inside the Toolkit</p>
            <h2 className="mt-6 max-w-3xl text-4xl font-extrabold text-navy lg:text-5xl">
              Everything You Need to Start Building a More Organized Business
            </h2>
          </FadeIn>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="cos-features">
            {FEATURES.map(({ icon: Icon, title, desc }, i) => (
              <FadeIn key={title} delay={Math.min(i * 0.04, 0.3)} className="h-full">
                <div className="h-full rounded-lg border border-navy/10 bg-paper p-6" data-testid={`cos-feature-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}>
                  <Icon className="h-5 w-5 text-tealdeep" />
                  <h3 className="mt-4 text-lg font-extrabold text-navy">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink/70">{desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="container-tsm max-w-[1200px]">
          <FadeIn>
            <p className="eyebrow">Product Demonstration</p>
            <h2 className="mt-6 max-w-3xl text-4xl font-extrabold text-navy lg:text-5xl">
              Not Just Templates. Tools You Can Actually Use.
            </h2>
            <p className="mt-4 max-w-2xl text-ink/70">
              The MITIVISE Toolkit is designed as a functional Excel workbook supported by editable Word templates —
              not a pile of blank spreadsheets.
            </p>
          </FadeIn>
          <FadeIn delay={0.1}>
            <ul className="mt-8 flex flex-wrap gap-2" data-testid="cos-functionality">
              {FUNCTIONALITY.map((f) => (
                <li key={f} className="inline-flex items-center gap-1.5 rounded-full border border-navy/10 bg-white px-4 py-2 text-xs font-semibold text-navy">
                  <CheckCircle2 className="h-3.5 w-3.5 text-tealdeep" />
                  {f}
                </li>
              ))}
            </ul>
          </FadeIn>
          <div className="mt-10 grid gap-6 lg:grid-cols-3" data-testid="cos-previews">
            <FadeIn className="h-full">
              <figure className="h-full overflow-hidden rounded-lg border border-navy/10 bg-paper">
                <img src="/previews/dashboard.png" alt="MITIVISE dashboard worksheet" className="w-full" loading="lazy" />
                <figcaption className="border-t border-navy/10 p-4">
                  <p className="text-lg font-extrabold text-navy">Compliance Dashboard</p>
                  <p className="mt-1 text-xs text-ink/55">Live metrics pulled from every register.</p>
                </figcaption>
              </figure>
            </FadeIn>
            <FadeIn delay={0.1} className="h-full">
              <figure className="h-full overflow-hidden rounded-lg border border-navy/10 bg-paper">
                <div>
                  <img src="/previews/risk-register.png" alt="MITIVISE risk register worksheet" className="w-full border-b border-navy/10" loading="lazy" />
                  <img src="/previews/risk-matrix.png" alt="Color-coded 5×5 risk heat map" className="w-full" loading="lazy" />
                </div>
                <figcaption className="border-t border-navy/10 p-4">
                  <p className="text-lg font-extrabold text-navy">Risk Register & Risk Heat Map</p>
                  <p className="mt-1 text-xs text-ink/55">Automatic scoring, severity colors, and the 5×5 matrix.</p>
                </figcaption>
              </figure>
            </FadeIn>
            <FadeIn delay={0.2} className="h-full">
              <figure className="h-full overflow-hidden rounded-lg border border-navy/10 bg-paper">
                <img src="/previews/audit-register.png" alt="MITIVISE audit register worksheet" className="w-full" loading="lazy" />
                <figcaption className="border-t border-navy/10 p-4">
                  <p className="text-lg font-extrabold text-navy">Audit Register</p>
                  <p className="mt-1 text-xs text-ink/55">From scheduling through findings and closure.</p>
                </figcaption>
              </figure>
            </FadeIn>
          </div>
          <p className="mt-4 text-center text-xs italic text-ink/45">
            Rendered from the actual MITIVISE workbook with sample entries — the files you receive are fully
            functional and editable.
          </p>
        </div>
      </section>

      <section className="border-y border-navy/5 bg-white py-20 lg:py-28">
        <div className="container-tsm max-w-[1200px]">
          <FadeIn>
            <p className="eyebrow">Who It's For</p>
            <h2 className="mt-6 max-w-3xl text-4xl font-extrabold text-navy lg:text-5xl">
              Built for the People Building Businesses.
            </h2>
          </FadeIn>
          <FadeIn delay={0.1}>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="cos-audiences">
              {AUDIENCES.map((a) => (
                <li key={a} className="flex h-full items-start gap-2.5 rounded-lg border border-navy/10 bg-paper p-4 text-sm font-medium text-navy/80">
                  <Briefcase className="mt-0.5 h-4 w-4 shrink-0 text-tealdeep" />
                  {a}
                </li>
              ))}
            </ul>
            <p className="mt-6 max-w-2xl text-ink/70">
              Whether you're operating alone or managing a growing team, MITIVISE provides a structured starting
              point for strengthening your internal business operations.
            </p>
            <p className="mt-3 max-w-2xl text-xs italic text-ink/50">
              The toolkit is a general business management resource, not an industry-specific legal compliance
              certification.
            </p>
          </FadeIn>
        </div>
      </section>

      <section id="tiers" className="py-20 lg:py-28">
        <div className="container-tsm max-w-[1200px]">
          <FadeIn className="max-w-3xl">
            <p className="eyebrow">MITIVISE Tiers</p>
            <h2 className="mt-6 text-4xl font-extrabold text-navy lg:text-5xl">Two ways to run MITIVISE.</h2>
          </FadeIn>
          <div className="mt-12 grid gap-6 lg:grid-cols-2" data-testid="cos-tiers">
            <FadeIn className="h-full">
              <div className="flex h-full flex-col rounded-xl border-2 border-teal bg-white p-8 shadow-[0_18px_40px_rgb(20,125,128,0.12)]" data-testid="cos-tier-toolkit">
                <p className="inline-flex w-fit items-center gap-2 rounded-full bg-teal px-4 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-white">
                  Available Now
                </p>
                <h3 className="mt-5 text-2xl font-extrabold tracking-tight text-navy">MITIVISE Toolkit</h3>
                <p className="mt-2 text-4xl font-extrabold text-navy">${price}<span className="ml-2 text-sm font-semibold text-ink/50">one-time payment</span></p>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-ink/65">
                  The complete downloadable business-readiness toolkit: risk management templates, audit trackers, SOP
                  organizational tools, license and insurance trackers, contract and vendor registers, and related
                  documents. Secure payment through Stripe with protected download access.
                </p>
                <button type="button" onClick={buy} disabled={busy} data-testid="cos-tier-toolkit-buy" className="btn-gold mt-7 w-full justify-center">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
                  Get the Toolkit — ${price}
                </button>
              </div>
            </FadeIn>
            <FadeIn delay={0.1} className="h-full">
              <div className="flex h-full flex-col rounded-xl border border-dashed border-navy/25 bg-paper p-8" data-testid="cos-tier-workspace">
                <p className="inline-flex w-fit items-center gap-2 rounded-full border border-dashed border-amberdeep/50 bg-amber/10 px-4 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-amberdeep">
                  In Development
                </p>
                <h3 className="mt-5 text-2xl font-extrabold tracking-tight text-navy">MITIVISE Workspace</h3>
                <p className="mt-2 text-2xl font-extrabold text-navy/70">Pricing announced at launch</p>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-ink/65">
                  A future monthly subscription that expands MITIVISE into an always-on digital workspace: ongoing
                  risk tracking, operational dashboards, SOP creation, deadline monitoring, and secure document
                  management. Details will be published when the Workspace is ready.
                </p>
                <p className="mt-7 rounded-lg border border-navy/10 bg-white px-4 py-3 text-xs leading-relaxed text-ink/55" data-testid="cos-tier-workspace-note">
                  Current toolkit customers will hear about the Workspace launch first. No payment is required or
                  accepted for the Workspace until it officially launches.
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <section id="buy" className="noise-dark relative bg-navy py-20 text-white lg:py-24">
        <div className="container-tsm relative max-w-[1200px]">
          <FadeIn>
            <p className="eyebrow">What You Receive</p>
            <h2 className="mt-6 text-4xl font-extrabold lg:text-5xl" data-testid="cos-included-title">
              MITIVISE Toolkit — <span className="text-gold">${price}</span>
            </h2>
            <p className="mt-3 text-sm font-semibold uppercase tracking-wider text-gold">One-Time Purchase — No Subscription Required</p>
            <ul className="mt-8 grid max-w-4xl gap-3 sm:grid-cols-2" data-testid="cos-included-list">
              {INCLUDED.map((item) => (
                <li key={item} className="flex items-start gap-3 rounded-lg border border-white/10 bg-white/5 p-4 text-sm text-white/80">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                  {item}
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-col items-start gap-4">
              <button type="button" onClick={buy} disabled={busy} data-testid="cos-buy-section-button" className="btn-gold !px-8 !py-4 text-base">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileDown className="h-5 w-5" />}
                Get the MITIVISE Toolkit — ${price}
              </button>
              <p className="text-xs leading-relaxed text-white/50">
                Checkout collects your name, email, and billing details securely through Stripe — no account required.
                Your secure download link appears instantly and is emailed to you; it stays active for{" "}
                {pricing ? pricing.toolkit_access_days : 30} days. Applicable taxes are calculated at checkout.
              </p>
              {error && <p className="text-sm text-amber-300" data-testid="cos-checkout-error">{error}</p>}
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="cos-sample-section">
        <div className="container-tsm grid max-w-[1200px] items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <FadeIn>
            <p className="eyebrow">Free Sample</p>
            <h2 className="mt-6 text-4xl font-extrabold text-navy lg:text-5xl">Peek inside the Toolkit before you buy.</h2>
            <p className="mt-4 max-w-xl text-ink/70">
              Download a free sample PDF from the toolkit: the full contents overview and a worked example from the risk
              register — so you know exactly what you're getting.
            </p>
            <ul className="mt-6 space-y-2.5">
              {["Full toolkit contents overview", "A worked risk register excerpt with sample scores", "The style of the annual review checklist"].map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-sm font-medium text-navy/80">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-tealdeep" />
                  {b}
                </li>
              ))}
            </ul>
          </FadeIn>
          <FadeIn delay={0.1}>
            <div className="rounded-lg border border-navy/10 bg-white p-6 lg:p-8">
              <form onSubmit={getSample} data-testid="sample-toolkit-form">
                <label className="block text-xs font-semibold uppercase tracking-wider text-navy/60" htmlFor="sample-email">Work email</label>
                <input
                  id="sample-email" type="email" required value={sample.email}
                  onChange={(e) => setSample({ ...sample, email: e.target.value })}
                  placeholder="you@yourbusiness.com" data-testid="sample-email-input"
                  className="mt-2 w-full rounded-sm border border-navy/15 bg-white px-4 py-3 text-sm text-navy outline-none transition-colors focus:border-teal"
                />
                <label className="mt-4 block text-xs font-semibold uppercase tracking-wider text-navy/60" htmlFor="sample-business">
                  Business name <span className="font-normal normal-case tracking-normal text-navy/40">(optional)</span>
                </label>
                <input
                  id="sample-business" type="text" value={sample.business_name}
                  onChange={(e) => setSample({ ...sample, business_name: e.target.value })}
                  placeholder="Your Business LLC" data-testid="sample-business-input"
                  className="mt-2 w-full rounded-sm border border-navy/15 bg-white px-4 py-3 text-sm text-navy outline-none transition-colors focus:border-teal"
                />
                <label className="mt-4 flex items-start gap-2.5 text-xs leading-relaxed text-ink/60">
                  <input
                    type="checkbox" required checked={sample.consent}
                    onChange={(e) => setSample({ ...sample, consent: e.target.checked })}
                    data-testid="sample-consent-checkbox" className="mt-0.5 h-4 w-4 shrink-0 accent-[#147D80]"
                  />
                  <span>Yes, email me the sample and occasional business readiness tips from TSM Group CLE. Unsubscribe anytime — we never sell your information.</span>
                </label>
                <button type="submit" disabled={sampleBusy} data-testid="sample-download-button" className="btn-gold mt-5 w-full justify-center">
                  <FileDown className="h-4 w-4" />
                  {sampleBusy ? "Preparing your sample…" : "Get the Free Sample PDF"}
                </button>
                {sampleDone && (
                  <p className="mt-3 text-sm font-semibold text-emerald-700" data-testid="sample-success">
                    Your sample is downloading — check your downloads folder.
                  </p>
                )}
                {sampleError && <p className="mt-3 text-sm text-red-700" data-testid="sample-error">{sampleError}</p>}
              </form>
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="border-y border-navy/5 bg-white py-20 lg:py-28">
        <div className="container-tsm max-w-[1200px]">
          <FadeIn>
            <p className="eyebrow">Still Deciding?</p>
            <h2 className="mt-6 max-w-2xl text-4xl font-extrabold text-navy lg:text-5xl">
              Not sure where you stand? Start with the free readiness assessment.
            </h2>
            <p className="mt-4 max-w-2xl text-ink/70">
              Answer eight plain-language questions and get a brief, personalized readiness summary — no account required.
            </p>
          </FadeIn>
          <FadeIn delay={0.15}>
            <div className="mt-9 flex flex-col gap-4 sm:flex-row">
              <Link to="/mitivise/assessment" data-testid="cos-assessment-cta" className="btn-gold">
                Take the Free Assessment
                <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="/contact" data-testid="cos-request-service" className="btn-outline">Request a Service</a>
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="container-tsm grid max-w-[1200px] gap-14 lg:grid-cols-12">
          <FadeIn className="lg:col-span-4">
            <p className="eyebrow">FAQ</p>
            <h2 className="mt-6 text-4xl font-extrabold leading-[1.08] tracking-tight text-navy sm:text-5xl" data-testid="cos-faq-headline">
              Questions, Answered
            </h2>
          </FadeIn>
          <FadeIn delay={0.15} className="lg:col-span-7 lg:col-start-6">
            <Accordion type="single" collapsible className="border-t border-navy/10" data-testid="cos-faq">
              {FAQS.map((faq, i) => (
                <AccordionItem key={faq.q} value={`cos-faq-${i}`} className="border-b border-navy/10">
                  <AccordionTrigger className="py-6 text-left text-xl font-extrabold leading-snug text-navy hover:no-underline hover:text-tealdeep sm:text-2xl [&>svg]:text-gold [&>svg]:h-5 [&>svg]:w-5" data-testid={`cos-faq-q-${i + 1}`}>
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 text-base leading-relaxed text-ink/70" data-testid={`cos-faq-a-${i + 1}`}>
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </FadeIn>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="container-tsm max-w-[1200px]">
          <FadeIn>
            <p className="eyebrow">TSM Group CLE Services</p>
            <h2 className="mt-6 max-w-3xl text-4xl font-extrabold text-navy lg:text-5xl">
              Need Help Putting Your Systems in Place?
            </h2>
            <p className="mt-4 max-w-2xl text-ink/70">
              MITIVISE gives you the tools to start organizing your business. When you need hands-on assistance,
              TSM Group CLE offers professional services to help you establish processes, improve documentation, and
              address operational risk concerns. Professional services are quoted and billed separately — they are not
              included in the $79 toolkit purchase.
            </p>
            <div className="mt-9 flex flex-col gap-4 sm:flex-row">
              <a href="/#services" data-testid="cos-explore-services" className="btn-gold">
                Explore TSM Services
                <ArrowRight className="h-4 w-4" />
              </a>
              <a href="/contact" data-testid="cos-request-assistance" className="btn-outline">Request Professional Assistance</a>
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="noise-dark relative bg-navy py-20 text-white lg:py-24">
        <div className="container-tsm relative flex flex-col items-center text-center">
          <FadeIn>
            <h2 className="mx-auto max-w-3xl text-4xl font-extrabold leading-tight sm:text-5xl" data-testid="cos-final-headline">
              Ready before readiness becomes <span className="text-gold">urgent.</span>
            </h2>
            <div className="mt-10 flex flex-col justify-center gap-4 sm:flex-row">
              <button type="button" onClick={buy} disabled={busy} data-testid="cos-final-buy" className="btn-gold">Get the Toolkit — ${price}</button>
              <Link to="/mitivise/assessment" data-testid="cos-final-assessment" className="btn-outline-light">Take the Free Assessment</Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
